"use client";

import React from "react";
import { Asset, AssetType, Item } from "@thenounproject/lingo-core";

import AssetPreviewColor from "./AssetPreviewColor";
import AssetPreviewFont from "./AssetPreviewFont";
import AssetPreviewAudio from "./AssetPreviewAudio";
import AssetPreviewMotion from "./AssetPreviewMotion";
import AssetPreviewImage from "./AssetPreviewImage";

export type AssetPreviewProps = {
  asset: Asset;
  item?: Item;

  // Motion assets autoplay when scrolled into view; also controls Lottie animation. Default: false
  autoplay?: boolean;

  // Show video controls instead of a static preview (motion assets only). Default: false
  showControls?: boolean;

  // Defer rendering until visible via IntersectionObserver. Default: true
  lazy?: boolean;

  onLoad?: (success: boolean) => void;
};

function AssetPreview({
  asset,
  item,
  autoplay = false,
  showControls = false,
  lazy = true,
  onLoad,
}: AssetPreviewProps) {
  const isMotion =
    AssetType.videoTypes.has(asset.type) ||
    asset.type === AssetType.gif ||
    asset.type === AssetType.lottie;

  if (asset.type === AssetType.color) {
    return <AssetPreviewColor asset={asset} lazy={lazy} />;
  }
  if (asset.type === AssetType.textStyle) {
    return <AssetPreviewFont asset={asset} lazy={lazy} />;
  }
  if (AssetType.audioTypes.has(asset.type)) {
    return <AssetPreviewAudio asset={asset} item={item} lazy={lazy} />;
  }
  if (isMotion) {
    return (
      <AssetPreviewMotion
        asset={asset}
        item={item}
        autoplay={autoplay}
        showControls={showControls}
        lazy={lazy}
      />
    );
  }
  return <AssetPreviewImage asset={asset} item={item} lazy={lazy} onLoad={onLoad} />;
}

export default React.memo(AssetPreview);
